import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import OfferCard from "./OfferCard";
import { SecondaryButton } from "../buttons";
import casinoService from "../../../services/casinoService";
import logger from "../../utils/logger";
import { useAuth } from "../../../contexts/AuthContext";

/**
 * OffersSection Component
 *
 * Displays a section of offers with configurable options:
 * - Number of offers to display
 * - Whether to show only featured offers
 * - Section title and description
 */
const OffersSection = ({
  title = "Special Offers",
  description = "Don't miss out on these amazing deals!",
  limit = 6,
  mobileLimit = null, // If set, limits offers shown on mobile
  featuredOnly = false,
  showViewAll = true,
  className = "",
  cardClassName = "",
  gridCols = "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
  filterType = null,
  fillLastTwoInRow = false,
}) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weeklyPurchaseHistory, setWeeklyPurchaseHistory] = useState({});
  const [timerData, setTimerData] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();

  useEffect(() => {
    // Only fetch offers if user is logged in
    if (isLoggedIn) {
      fetchOffers();
    } else {
      // Set empty state for non-logged-in users
      setOffers([]);
      setWeeklyPurchaseHistory({});
      setLoading(false);
      setError(null);
    }
  }, [limit, featuredOnly, filterType, isLoggedIn]);

  // Track screen size for responsive offer limiting
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const fetchOffers = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch offers, weekly purchase history, and timer data in parallel
      const [offersResponse, weeklyHistoryResponse, timerDataResponse] =
        await Promise.all([
          casinoService.getOffers({
            limit,
            featured: featuredOnly,
            filterType,
          }),
          casinoService.getWeeklyPurchaseHistory().catch((error) => {
            logger.warn(
              "OffersSection: Failed to fetch weekly purchase history",
              error,
            );
            return {}; // Return empty object if fails
          }),
          casinoService.getTimerData().catch((error) => {
            logger.warn("OffersSection: Failed to fetch timer data", error);
            return null; // Return null if fails
          }),
        ]);

      const response = offersResponse;

      // Handle response structure: backend returns { data: [...], meta: {...} }
      let offersData = [];
      if (response && response.data && Array.isArray(response.data)) {
        offersData = response.data;
      } else {
        logger.error(
          "OffersSection: Invalid response format - data is not an array",
          response,
        );
        setError("No offers data received");
        return;
      }

      // Apply client-side filtering based on filterType
      if (filterType) {
        logger.debug("Filtering offers by type:", filterType);
        logger.debug(
          "All offers before filtering:",
          offersData.map((o) => ({ name: o.name, badge_type: o.badge_type })),
        );

        offersData = offersData.filter((offer) => {
          switch (filterType) {
            case "first_time": {
              // Check for any first-time bonus offers (1st Purchase, 2nd Purchase, etc.)
              const isFirstTime =
                offer.badge_type === "first_time_bonus" ||
                (offer.name &&
                  (offer.name.toLowerCase().includes("1st purchase") ||
                    offer.name.toLowerCase().includes("2nd purchase")));
              logger.debug(
                `${offer.name}: isFirstTime=${isFirstTime}, badge_type=${offer.badge_type}`,
              );
              return isFirstTime;
            }
            case "second_time": {
              // Check for 2nd Purchase in name
              const isSecondTime =
                offer.name && offer.name.toLowerCase().includes("2nd purchase");
              logger.debug(`${offer.name}: isSecondTime=${isSecondTime}`);
              return isSecondTime;
            }
            case "weekly_special": {
              // Check badge_type for "weekly_special" or name containing "weekly"
              const isWeekly =
                (offer.badge_type && offer.badge_type === "weekly_special") ||
                (offer.name && offer.name.toLowerCase().includes("weekly"));
              logger.debug(
                `${offer.name}: badge_type="${offer.badge_type}", isWeekly=${isWeekly}`,
              );
              return isWeekly;
            }
            case "other": {
              // Exclude 1st purchase, 2nd purchase and weekly offers
              const isFirstTimeOther =
                offer.name && offer.name.toLowerCase().includes("1st purchase");
              const isSecondTimeOther =
                offer.name && offer.name.toLowerCase().includes("2nd purchase");
              const isWeeklyOther =
                (offer.badge_type && offer.badge_type === "weekly_special") ||
                (offer.name && offer.name.toLowerCase().includes("weekly"));
              const isOther =
                !isFirstTimeOther && !isSecondTimeOther && !isWeeklyOther;
              logger.debug(`${offer.name}: isOther=${isOther}`);
              return isOther;
            }
            default:
              return true;
          }
        });

        logger.debug(
          "Filtered offers:",
          offersData.map((o) => ({
            name: o.name,
            badge_type: o.badge_type,
            weekly_limit: o.weekly_limit,
          })),
        );
      }

      // Set weekly purchase history and timer data
      setWeeklyPurchaseHistory(weeklyHistoryResponse || {});
      setTimerData(timerDataResponse);

      setOffers(offersData);
    } catch (err) {
      logger.error("OffersSection: Error fetching offers", err);
      setError(`Failed to load offers: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBuyNow = (offer) => {
    // Navigate to secure checkout page
    navigate(`/checkout/${offer.id}`);
  };

  const handleViewAll = () => {
    // Navigate to all offers page
    navigate("/offers");
  };

  // Don't render anything for non-logged-in users
  if (!isLoggedIn) {
    return null;
  }

  if (loading) {
    return (
      <section className={`py-8 ${className}`}>
        <div className="container mx-auto px-4">
          {/* Section Header - Only show if title or description exists */}
          {(title || description) && (
            <div className="text-center mb-6">
              {title && (
                <h2 className="text-2xl font-bold text-white mb-2">{title}</h2>
              )}
              {description && (
                <p className="text-gray-400 max-w-2xl mx-auto text-sm">
                  {description}
                </p>
              )}
            </div>
          )}

          {/* Loading skeleton - Dark theme matching OfferCard structure */}
          <div className={`grid ${gridCols} gap-4`}>
            {Array.from({ length: limit || 1 }).map((_, index) => (
              <div
                key={index}
                className="relative rounded-xl shadow-lg overflow-hidden animate-pulse offers-skeleton-bg"
              >
                {/* Content Container */}
                <div className="relative z-10 flex flex-col h-full">
                  {/* Main Content Area - Matches OfferCard layout */}
                  <div className="flex-1 p-6 relative">
                    {/* Background Package Image Placeholder - Right aligned */}
                    <div className="absolute right-0 top-0 w-64 h-full z-0">
                      <div className="w-full h-full bg-card/30 rounded"></div>
                    </div>

                    {/* Text Content Placeholders - Over the background */}
                    <div className="relative z-10">
                      {/* Gold Coins - Large placeholder */}
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-gray-600/50 rounded-full flex-shrink-0"></div>
                        <div className="h-12 bg-gray-600/50 rounded w-32"></div>
                      </div>

                      {/* CB Coins - Medium placeholder */}
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-600/50 rounded-full flex-shrink-0"></div>
                        <div className="h-6 bg-gray-600/50 rounded w-40"></div>
                      </div>
                    </div>
                  </div>

                  {/* Footer - Matches OfferCard footer */}
                  <div className="bg-black/80 px-6 py-4 border-t border-white/10 flex-shrink-0">
                    <div className="flex items-center justify-between">
                      {/* Left side - Package name and terms placeholders */}
                      <div className="flex-1">
                        <div className="h-5 bg-gray-600/50 rounded w-32 mb-1"></div>
                        <div className="h-4 bg-gray-600/50 rounded w-24 mb-1"></div>
                        <div className="h-3 bg-gray-600/50 rounded w-20"></div>
                      </div>

                      {/* Right side - Price placeholder */}
                      <div className="h-6 bg-gray-600/50 rounded w-16"></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className={`py-8 ${className}`}>
        <div className="container mx-auto px-4">
          <div className="text-center">
            {title && (
              <h2 className="text-2xl font-bold text-white mb-4">{title}</h2>
            )}
            <p className="text-red-400 mb-4">{error}</p>
            <button
              onClick={fetchOffers}
              className="bg-primary hover:bg-primary/90 text-black font-bold py-2 px-4 rounded-lg transition-colors cursor-pointer"
            >
              Try Again
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (!Array.isArray(offers) || offers.length === 0) {
    return (
      <section className={`py-8 ${className}`}>
        <div className="container mx-auto px-4">
          <div className="text-center">
            {title && (
              <h2 className="text-2xl font-bold text-white mb-4">{title}</h2>
            )}
            <p className="text-gray-400">No offers available at the moment.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={className || "py-8"}>
      {/* Section Header - Only show if title or description exists */}
      {(title || description) && (
        <div className="text-center mb-6">
          {title && (
            <h2 className="text-2xl font-bold text-white mb-2">{title}</h2>
          )}
          {description && (
            <p className="text-gray-400 max-w-2xl mx-auto text-sm">
              {description}
            </p>
          )}
        </div>
      )}

      {/* Offers Grid */}
      <div className={`grid auto-rows-fr ${gridCols} gap-4`}>
        {Array.isArray(offers)
          ? (() => {
              // Apply responsive limiting: show fewer offers on mobile if mobileLimit is set
              const displayOffers =
                mobileLimit && isMobile ? offers.slice(0, mobileLimit) : offers;

              // Determine desktop column count from gridCols (defaults to 3)
              const match = gridCols.match(/md:grid-cols-(\d+)/);
              const desktopCols = match ? parseInt(match[1], 10) : 3;

              return displayOffers.map((offer, index) => {
                const shouldSpan =
                  fillLastTwoInRow &&
                  !isMobile &&
                  displayOffers.length % desktopCols === 2 &&
                  index >= displayOffers.length - 2;
                const wrapperClass = shouldSpan ? "md:col-span-2" : "";

                return (
                  <div key={offer.id} className={`${wrapperClass} h-full`}>
                    <OfferCard
                      offer={offer}
                      onBuyNow={handleBuyNow}
                      className={`${cardClassName} h-full`}
                      showTimer={
                        filterType === "first_time" ||
                        filterType === "second_time" ||
                        (offer.name &&
                          offer.name.toLowerCase().includes("1st purchase")) ||
                        (offer.name &&
                          offer.name.toLowerCase().includes("2nd purchase")) ||
                        (offer.name &&
                          offer.name.toLowerCase().includes("first time")) ||
                        (offer.name &&
                          offer.name.toLowerCase().includes("second time"))
                      }
                      lastPurchaseTime={
                        weeklyPurchaseHistory[offer.id]?.lastPurchaseTime
                      }
                      showWeeklyTimer={
                        (offer.badge_type === "weekly_special" ||
                          offer.weekly_limit) &&
                        weeklyPurchaseHistory[offer.id]?.lastPurchaseTime
                      }
                      timerData={timerData}
                    />
                  </div>
                );
              });
            })()
          : null}
      </div>

      {/* View All Button */}
      {showViewAll && (
        <div className="text-center">
          <SecondaryButton onClick={handleViewAll} size="medium">
            View All Offers
          </SecondaryButton>
        </div>
      )}
    </section>
  );
};

export default OffersSection;
